import { useState, useRef, useEffect, useCallback } from 'react';
import {
  Bold, Italic, Underline, List, ListOrdered, Image as ImageIcon, Download, Save,
  ArrowLeft, AlignLeft, AlignCenter, AlignRight, Link, ChevronDown, Info, Plus, Loader2,
  Sparkles, Eye, EyeOff, Highlighter, Calculator, LayoutTemplate, Check,
} from 'lucide-react';
import { api } from '../lib/api';
import { printWorksheet } from '../lib/exportPdf';
import { WORKSHEET_CSS, GRADE_PRESETS, presetById, FRAMES, FONTS, fontById } from '../lib/worksheetStyles';
import { BLOCK_DEFS, blockHtml, ruleBlock, imageFigure, mathProblems, TEMPLATES, type BlockType, type MathOp } from '../lib/worksheetBlocks';
import { getSettings } from '../lib/settings';

interface EditorViewProps {
  sheetId?: string;
  folderId?: string;
  initialTitle?: string;
  initialSubject?: string;
  initialGrade?: string;
  onSaved?: () => void;
  onBack?: () => void;
}

function exec(cmd: string, val?: string) {
  document.execCommand(cmd, false, val);
}
function getActiveStyles() {
  return {
    bold: document.queryCommandState('bold'),
    italic: document.queryCommandState('italic'),
    underline: document.queryCommandState('underline'),
    justifyLeft: document.queryCommandState('justifyLeft'),
    justifyCenter: document.queryCommandState('justifyCenter'),
    justifyRight: document.queryCommandState('justifyRight'),
    insertUnorderedList: document.queryCommandState('insertUnorderedList'),
    insertOrderedList: document.queryCommandState('insertOrderedList'),
    formatBlock: (document.queryCommandValue('formatBlock') as string) || '',
  };
}

export default function EditorView({
  sheetId, folderId, initialTitle, initialSubject, initialGrade, onSaved, onBack,
}: EditorViewProps) {
  // ── Meta ───────────────────────────────────────────────────
  const [title, setTitle] = useState(initialTitle || '');
  const [subject, setSubject] = useState(initialSubject || '');
  const [grade, setGrade] = useState(initialGrade || '');
  const [schoolYear, setSchoolYear] = useState('');
  const [teacher, setTeacher] = useState('');
  const [gradeLevel, setGradeLevel] = useState('kl34');
  const [frame, setFrame] = useState('none');
  const [fontFamily, setFontFamily] = useState('standard');

  // ── UI ─────────────────────────────────────────────────────
  const [docId, setDocId] = useState<string | undefined>(sheetId);
  const [isLoading, setIsLoading] = useState(!!sheetId);
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [status, setStatus] = useState<'saved' | 'unsaved' | 'saving' | 'error'>('saved');
  const [error, setError] = useState<string | null>(null);
  const [styles, setStyles] = useState(getActiveStyles());
  const [showHeading, setShowHeading] = useState(false);
  const [showInsert, setShowInsert] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [outline, setOutline] = useState<{ level: number; text: string }[]>([]);
  const [showSolutions, setShowSolutions] = useState(true);
  const [pageCount, setPageCount] = useState(1);

  // KI-Assistent
  const [aiKind, setAiKind] = useState<'lesetext' | 'aufgaben' | 'lueckentext' | 'quiz'>('aufgaben');
  const [aiTopic, setAiTopic] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  // Mathe-Generator
  const [mathOp, setMathOp] = useState<MathOp>('add');
  const [mathMax, setMathMax] = useState(20);
  const [mathCount, setMathCount] = useState(8);

  const editorRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const insertRef = useRef<HTMLDivElement>(null);
  const templatesRef = useRef<HTMLDivElement>(null);
  const pagesContentRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const savedRange = useRef<Range | null>(null);
  const pendingHtml = useRef<string | null>(null);
  // Autosave-Steuerung
  const [changeTick, setChangeTick] = useState(0);
  const unsavedRef = useRef(false);
  const loadedRef = useRef(false);
  const saveRef = useRef<((opts?: { navigate?: boolean }) => void) | null>(null);

  const preset = presetById(gradeLevel);

  // Bausteinstile einmalig injizieren
  useEffect(() => {
    if (!document.getElementById('ws-style')) {
      const s = document.createElement('style');
      s.id = 'ws-style';
      s.textContent = WORKSHEET_CSS;
      document.head.appendChild(s);
    }
  }, []);

  // ── Laden ──────────────────────────────────────────────────
  useEffect(() => {
    let active = true;
    (async () => {
      if (sheetId) {
        try {
          const ws = await api.worksheets.get(sheetId);
          if (!active) return;
          const content = ws.content || {};
          const html = typeof content === 'string' ? content : (content.html ?? '');
          const meta = (typeof content === 'object' && content.meta) || {};
          setTitle(ws.title || '');
          setSubject(meta.subject ?? ws.settings?.subject ?? '');
          setGrade(meta.grade ?? ws.settings?.grade ?? '');
          setSchoolYear(meta.schoolYear ?? '');
          setTeacher(meta.teacher ?? '');
          if (meta.gradeLevel) setGradeLevel(meta.gradeLevel);
          if (meta.frame) setFrame(meta.frame);
          if (meta.fontFamily) setFontFamily(meta.fontFamily);
          // Inhalt erst nach dem Mounten einspielen (siehe Effect unten),
          // da der Editor während isLoading noch nicht gerendert ist.
          pendingHtml.current = html || '<p><br></p>';
        } catch (err: any) {
          if (active) setError('Laden fehlgeschlagen: ' + err.message);
        } finally {
          if (active) setIsLoading(false);
        }
      } else {
        if (editorRef.current) editorRef.current.innerHTML = '<p><br></p>';
        // Neue Blätter mit Profil-Voreinstellungen vorbefüllen
        const s = getSettings();
        if (s.teacher) setTeacher(s.teacher);
        if (s.schoolYear) setSchoolYear(s.schoolYear);
      }
      if (active) {
        updateOutline();
        // ab jetzt zählen Änderungen als „ungespeichert"
        loadedRef.current = true;
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sheetId]);

  // Selektion / Toolbar-Status
  useEffect(() => {
    const handler = () => setStyles(getActiveStyles());
    document.addEventListener('selectionchange', handler);
    return () => document.removeEventListener('selectionchange', handler);
  }, []);

  // Dropdowns schließen bei Klick außerhalb
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (headingRef.current && !headingRef.current.contains(e.target as Node)) setShowHeading(false);
      if (insertRef.current && !insertRef.current.contains(e.target as Node)) setShowInsert(false);
      if (templatesRef.current && !templatesRef.current.contains(e.target as Node)) setShowTemplates(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const updateOutline = useCallback(() => {
    if (!editorRef.current) return;
    const hs: { level: number; text: string }[] = [];
    editorRef.current.querySelectorAll('h1,h2,h3').forEach((h) => {
      hs.push({ level: h.tagName === 'H1' ? 1 : h.tagName === 'H2' ? 2 : 3, text: h.textContent || '' });
    });
    setOutline(hs);
  }, []);

  // Geladenen Inhalt einspielen, sobald der Editor gemountet ist (nach isLoading=false).
  useEffect(() => {
    if (!isLoading && pendingHtml.current !== null && editorRef.current) {
      editorRef.current.innerHTML = pendingHtml.current;
      pendingHtml.current = null;
      updateOutline();
    }
  }, [isLoading, updateOutline]);

  // ── Selektion merken / Einfügen am Cursor ──────────────────
  const rememberSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && editorRef.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const insertHtml = (html: string) => {
    const ed = editorRef.current;
    if (!ed) return;
    ed.focus();
    const sel = window.getSelection();
    // Gespeicherte Auswahl wiederherstellen, sonst ans Ende setzen
    if (savedRange.current && ed.contains(savedRange.current.commonAncestorContainer)) {
      sel?.removeAllRanges();
      sel?.addRange(savedRange.current);
    } else {
      const range = document.createRange();
      range.selectNodeContents(ed);
      range.collapse(false);
      sel?.removeAllRanges();
      sel?.addRange(range);
    }
    exec('insertHTML', html);
    savedRange.current = null;
    setStyles(getActiveStyles());
    updateOutline();
    markChanged();
  };

  const insertBlock = (type: BlockType) => {
    setShowInsert(false);
    const html = type === 'rule' ? ruleBlock(preset.defaultRule, 4) + '<p><br></p>' : blockHtml(type);
    insertHtml(html);
  };

  const onPickImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const dataUrl: string = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
    try {
      // Upload ins MinIO → schlanke Referenz statt base64 im Dokument
      const { url } = await api.images.upload(dataUrl);
      insertHtml(imageFigure(window.location.origin + url));
    } catch {
      // Fallback: Bild direkt einbetten, damit der Upload-Fehler nicht blockiert
      insertHtml(imageFigure(dataUrl));
    }
  };

  // ── Lösung markieren ───────────────────────────────────────
  const markSolution = () => {
    const sel = window.getSelection();
    const text = sel?.toString();
    if (!text) return;
    const escaped = text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    rememberSelection();
    insertHtml(`<span class="ws-solution">${escaped}</span>`);
  };

  // ── KI-Assistent ───────────────────────────────────────────
  const handleGenerateAI = async () => {
    if (!aiTopic.trim() || aiBusy) return;
    setAiBusy(true);
    setAiError(null);
    try {
      const gradeLabel = presetById(gradeLevel).label;
      const { html } = await api.ai.generate({ kind: aiKind, topic: aiTopic, grade: grade || gradeLabel });
      insertHtml(html);
      setAiTopic('');
    } catch (err: any) {
      setAiError(err.message || 'KI-Generierung fehlgeschlagen');
    } finally {
      setAiBusy(false);
    }
  };

  // ── Mathe-Generator ────────────────────────────────────────
  const insertMath = () => {
    insertHtml(mathProblems(mathOp, mathMax, mathCount));
  };

  // ── Vorlage einfügen ───────────────────────────────────────
  const insertTemplate = (id: string) => {
    setShowTemplates(false);
    const t = TEMPLATES.find((x) => x.id === id);
    if (!t) return;
    if (!title.trim()) setTitle(t.title);
    insertHtml(t.html);
  };

  // ── Änderung markieren (treibt Autosave) ───────────────────
  const markChanged = useCallback(() => {
    if (!loadedRef.current) return;
    unsavedRef.current = true;
    setStatus('unsaved');
    setChangeTick((t) => t + 1);
  }, []);

  const changeGradeLevel = (id: string) => { setGradeLevel(id); markChanged(); };
  const changeFrame = (id: string) => { setFrame(id); markChanged(); };
  const changeFont = (id: string) => { setFontFamily(id); markChanged(); };

  // ── Speichern (Autosave & manuell) ─────────────────────────
  const save = useCallback(async (opts?: { navigate?: boolean }) => {
    if (isSaving) return;
    const html = editorRef.current?.innerHTML || '';
    if (!docId && !folderId) {
      if (opts?.navigate) setError('Kein Ordner ausgewählt');
      return;
    }
    const settings = { pageSize: 'A4', orientation: 'portrait', subject, grade };
    const content = { html, meta: { subject, grade, schoolYear, teacher, gradeLevel, frame, fontFamily } };
    setIsSaving(true);
    setStatus('saving');
    setError(null);
    try {
      if (docId) {
        await api.worksheets.update(docId, { title: title || 'Unbenannt', content, settings });
      } else if (folderId) {
        const created = await api.worksheets.create({ folderId, title: title || 'Unbenannt', content, settings });
        setDocId(created.id);
      }
      unsavedRef.current = false;
      setStatus('saved');
      if (opts?.navigate) onSaved?.();
    } catch (err: any) {
      setStatus('error');
      setError('Speichern fehlgeschlagen: ' + err.message);
    } finally {
      setIsSaving(false);
    }
  }, [isSaving, docId, folderId, title, subject, grade, schoolYear, teacher, gradeLevel, frame, fontFamily, onSaved]);
  saveRef.current = save;

  // Debounced Autosave: 1,5 s nach der letzten Änderung
  useEffect(() => {
    if (changeTick === 0) return;
    const t = setTimeout(() => {
      if (unsavedRef.current && !isSaving) saveRef.current?.({});
    }, 1500);
    return () => clearTimeout(t);
  }, [changeTick, isSaving]);

  // Warnung bei ungespeicherten Änderungen beim Verlassen
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => {
      if (unsavedRef.current) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, []);

  // Seitenzahl aus der Inhaltshöhe ableiten (1 A4-Seite = 297mm)
  useEffect(() => {
    const el = pagesContentRef.current;
    if (!el) return;
    const PAGE_PX = (297 * 96) / 25.4; // 297mm in CSS-px
    const update = () => setPageCount(Math.max(1, Math.ceil(el.scrollHeight / PAGE_PX)));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [isLoading]);

  // ── PDF ────────────────────────────────────────────────────
  const handleExport = async () => {
    if (isExporting) return;
    const html = editorRef.current?.innerHTML || '';
    setIsExporting(true);
    try {
      await printWorksheet(html, {
        title, subject, grade, schoolYear, teacher, font: preset.font, lh: preset.lh, frame,
        solutions: showSolutions, family: fontById(fontFamily).stack,
      });
    } finally {
      setIsExporting(false);
    }
  };

  // ── Toolbar-Helfer ─────────────────────────────────────────
  const headingLabel = styles.formatBlock === 'h1' || styles.formatBlock === 'H1' ? 'Überschrift 1'
    : styles.formatBlock === 'h2' || styles.formatBlock === 'H2' ? 'Überschrift 2'
    : styles.formatBlock === 'h3' || styles.formatBlock === 'H3' ? 'Überschrift 3'
    : 'Fließtext';

  const ToolBtn = ({ icon: Icon, on, onClick, title: t }: { icon: React.ElementType; on?: boolean; onClick?: () => void; title?: string }) => (
    <button onPointerDown={(e) => e.preventDefault()} onClick={onClick} title={t}
      className={`w-8 h-8 rounded-lg inline-flex items-center justify-center transition-colors cursor-pointer ${on ? 'bg-cobalt-soft text-cobalt-deep' : 'hover:bg-paper-deep text-ink-soft'}`}>
      <Icon className="w-4 h-4" strokeWidth={on ? 2.5 : 1.75} />
    </button>
  );
  const Sep = () => <span className="w-px h-5 bg-rule mx-1" />;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 text-cobalt animate-spin" />
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────
  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-5 items-start">
      <div className="flex flex-col min-w-0">
        {/* Toolbar */}
        <div className="sl-card mb-4 p-2 flex items-center gap-1 flex-wrap shrink-0 sticky top-0 z-30">
          {onBack && (
            <button onClick={onBack} className="p-2 rounded-lg hover:bg-paper-deep transition-colors mr-1" title="Zurück">
              <ArrowLeft className="w-4 h-4 text-ink-mute" />
            </button>
          )}
          <Sep />
          {/* Überschrift */}
          <div className="relative" ref={headingRef}>
            <button onPointerDown={(e) => e.preventDefault()} onClick={() => setShowHeading(!showHeading)}
              className="h-8 px-3 rounded-lg hover:bg-paper-deep transition-colors text-sm font-semibold text-ink flex items-center gap-1">
              {headingLabel}<ChevronDown className="w-3 h-3 text-ink-mute" />
            </button>
            {showHeading && (
              <div className="absolute top-full left-0 mt-1 bg-card border border-rule rounded-xl shadow-elevation-2 p-1 z-50 min-w-[160px]">
                {[{ label: 'Fließtext', tag: 'p' }, { label: 'Überschrift 1', tag: 'h1' }, { label: 'Überschrift 2', tag: 'h2' }, { label: 'Überschrift 3', tag: 'h3' }].map((h) => (
                  <button key={h.tag} onPointerDown={(e) => e.preventDefault()}
                    onClick={() => { exec('formatBlock', h.tag); setShowHeading(false); updateOutline(); }}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm transition-colors hover:bg-paper-deep text-ink">{h.label}</button>
                ))}
              </div>
            )}
          </div>
          <Sep />
          <ToolBtn icon={Bold} on={styles.bold} onClick={() => exec('bold')} title="Fett" />
          <ToolBtn icon={Italic} on={styles.italic} onClick={() => exec('italic')} title="Kursiv" />
          <ToolBtn icon={Underline} on={styles.underline} onClick={() => exec('underline')} title="Unterstrichen" />
          <Sep />
          <ToolBtn icon={AlignLeft} on={styles.justifyLeft} onClick={() => exec('justifyLeft')} title="Links" />
          <ToolBtn icon={AlignCenter} on={styles.justifyCenter} onClick={() => exec('justifyCenter')} title="Zentriert" />
          <ToolBtn icon={AlignRight} on={styles.justifyRight} onClick={() => exec('justifyRight')} title="Rechts" />
          <Sep />
          <ToolBtn icon={List} on={styles.insertUnorderedList} onClick={() => exec('insertUnorderedList')} title="Aufzählung" />
          <ToolBtn icon={ListOrdered} on={styles.insertOrderedList} onClick={() => exec('insertOrderedList')} title="Nummerierte Liste" />
          <ToolBtn icon={Link} onClick={() => { const u = prompt('URL eingeben:'); if (u) exec('createLink', u); }} title="Link" />
          <Sep />
          {/* Lösung markieren + Lösungen ein/aus */}
          <ToolBtn icon={Highlighter} onClick={markSolution} title="Auswahl als Lösung markieren" />
          <ToolBtn icon={showSolutions ? Eye : EyeOff} on={!showSolutions} onClick={() => setShowSolutions((s) => !s)} title={showSolutions ? 'Lösungen ausblenden' : 'Lösungen anzeigen'} />
          <Sep />
          {/* Vorlagen */}
          <div className="relative" ref={templatesRef}>
            <button onPointerDown={(e) => { e.preventDefault(); rememberSelection(); }} onClick={() => setShowTemplates(!showTemplates)}
              className="h-8 px-3 rounded-lg hover:bg-paper-deep transition-colors text-sm font-semibold text-ink inline-flex items-center gap-1.5">
              <LayoutTemplate className="w-4 h-4 text-ink-mute" />Vorlagen<ChevronDown className="w-3 h-3 text-ink-mute" />
            </button>
            {showTemplates && (
              <div className="absolute top-full left-0 mt-1 bg-card border border-rule rounded-xl shadow-elevation-2 p-1 z-50 w-[200px]">
                {TEMPLATES.map((t) => (
                  <button key={t.id} onPointerDown={(e) => e.preventDefault()} onClick={() => insertTemplate(t.id)}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm transition-colors hover:bg-paper-deep text-ink">{t.label}</button>
                ))}
              </div>
            )}
          </div>
          <Sep />
          {/* Einfügen */}
          <div className="relative" ref={insertRef}>
            <button onPointerDown={(e) => { e.preventDefault(); rememberSelection(); }} onClick={() => setShowInsert(!showInsert)}
              className="h-8 px-3 rounded-lg bg-cobalt-soft text-cobalt-deep text-xs font-bold inline-flex items-center gap-1.5 hover:opacity-80 transition-opacity">
              <Plus className="w-3.5 h-3.5" />Einfügen<ChevronDown className="w-3 h-3" />
            </button>
            {showInsert && (
              <div className="absolute top-full left-0 mt-1 bg-card border border-rule rounded-xl shadow-elevation-2 p-1 z-50 w-[230px] max-h-[60vh] overflow-auto">
                {BLOCK_DEFS.map((b) => (
                  <button key={b.type} onPointerDown={(e) => e.preventDefault()} onClick={() => insertBlock(b.type)}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-paper-deep transition-colors">
                    <div className="text-sm font-semibold text-ink">{b.label}</div>
                    <div className="text-[11px] text-ink-mute">{b.hint}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <ToolBtn icon={ImageIcon} onClick={() => { rememberSelection(); fileRef.current?.click(); }} title="Bild hochladen" />
          <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPickImage} />

          {/* Klassenstufe & Rahmen – nur mobil (rechte Spalte ist auf dem Handy ausgeblendet) */}
          <select
            value={gradeLevel}
            onChange={(e) => changeGradeLevel(e.target.value)}
            className="lg:hidden h-8 px-2 rounded-lg border border-rule bg-card text-xs font-semibold text-ink-soft focus:outline-none"
            title="Klassenstufe"
          >
            {GRADE_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <select
            value={frame}
            onChange={(e) => changeFrame(e.target.value)}
            className="lg:hidden h-8 px-2 rounded-lg border border-rule bg-card text-xs font-semibold text-ink-soft focus:outline-none"
            title="Rahmen"
          >
            {FRAMES.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>

          <div className="flex-1 min-w-4" />
          {/* Autosave-Status */}
          <span className="hidden sm:inline-flex items-center gap-1.5 text-xs text-ink-mute mr-1 select-none" title="Automatisch gespeichert">
            {status === 'saving' && <><Loader2 className="w-3.5 h-3.5 animate-spin" />Speichert…</>}
            {status === 'saved' && <><Check className="w-3.5 h-3.5 text-sage" />Gespeichert</>}
            {status === 'unsaved' && <><span className="w-2 h-2 rounded-full bg-sun-deep" />Ungespeichert</>}
            {status === 'error' && <span className="text-coral">Fehler</span>}
          </span>
          <button onClick={() => save({ navigate: true })} disabled={isSaving} className="sl-button sl-button-primary !h-8 gap-2 disabled:opacity-50">
            {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Fertig
          </button>
          <button onClick={handleExport} disabled={isExporting} className="sl-button sl-button-outline !h-8 gap-2 disabled:opacity-50">
            {isExporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}PDF
          </button>
        </div>

        {error && <div className="sl-card p-3 mb-4 border-coral bg-coral-soft text-coral text-sm">{error}</div>}

        {/* A4-Seite(n) */}
        <div className="editor-desk overflow-x-auto">
          <div className="ws-pages" style={{ minHeight: `${pageCount * 297}mm` }}>
            {/* Backdrop: je Seite ein eigenes Blatt mit eigenem Rahmen */}
            {Array.from({ length: pageCount }).map((_, i) => (
              <div key={i} className="ws-sheet" style={{ top: `${i * 297}mm` }} aria-hidden>
                <div className={`ws-sheet-frame ws-frame--${frame}`} />
                <span className="ws-page-label">Seite {i + 1}</span>
              </div>
            ))}

            {/* Inhalt über den Blättern */}
            <div className="ws-pages-content" ref={pagesContentRef}>
              {/* Kopf */}
              <input value={title} onChange={(e) => { setTitle(e.target.value); markChanged(); }}
                className="w-full text-3xl font-bold text-cobalt bg-transparent border-0 p-0 mb-3 focus:outline-none"
                style={{ lineHeight: 1.15, letterSpacing: '-0.01em' }} placeholder="Titel des Arbeitsblatts" />
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-ink-soft border-b-2 border-cobalt pb-2.5 mb-3">
                <span className="inline-flex items-center gap-1"><b>Fach:</b>
                  <input value={subject} onChange={(e) => { setSubject(e.target.value); markChanged(); }} placeholder="—" className="bg-transparent border-0 p-0 w-24 focus:outline-none" /></span>
                <span className="inline-flex items-center gap-1"><b>Klasse:</b>
                  <input value={grade} onChange={(e) => { setGrade(e.target.value); markChanged(); }} placeholder="—" className="bg-transparent border-0 p-0 w-16 focus:outline-none" /></span>
                <span className="inline-flex items-center gap-1"><b>Schuljahr:</b>
                  <input value={schoolYear} onChange={(e) => { setSchoolYear(e.target.value); markChanged(); }} placeholder="—" className="bg-transparent border-0 p-0 w-20 focus:outline-none" /></span>
                <span className="inline-flex items-center gap-1 ml-auto"><b>Lehrkraft:</b>
                  <input value={teacher} onChange={(e) => { setTeacher(e.target.value); markChanged(); }} placeholder="—" className="bg-transparent border-0 p-0 w-28 focus:outline-none" /></span>
              </div>
              <div className="flex gap-8 text-[13px] text-ink-mute mb-4">
                <span className="flex-1">Name: <span className="inline-block border-b border-ink-mute" style={{ minWidth: 120 }}>&nbsp;</span></span>
                <span>Datum: <span className="inline-block border-b border-ink-mute" style={{ minWidth: 90 }}>&nbsp;</span></span>
              </div>

              {/* Inhalt */}
              <div
                ref={editorRef}
                contentEditable
                suppressContentEditableWarning
                className={`ws-content min-h-[220mm] outline-none ${showSolutions ? '' : 'hide-solutions'}`}
                style={{ '--ws-font': preset.font, '--ws-lh': preset.lh, '--ws-family': fontById(fontFamily).stack } as React.CSSProperties}
                onInput={() => { setStyles(getActiveStyles()); updateOutline(); markChanged(); }}
                onMouseUp={() => { setStyles(getActiveStyles()); rememberSelection(); }}
                onKeyUp={() => { setStyles(getActiveStyles()); rememberSelection(); }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Rechtes Rail — auf dem Handy ausgeblendet */}
      <aside className="hidden lg:flex flex-col gap-4 sticky top-0 h-fit">
        {/* KI-Assistent */}
        <div className="sl-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles className="w-3.5 h-3.5 text-lilac" />
            <span className="text-xs font-bold text-ink-soft uppercase tracking-wide">KI-Assistent</span>
          </div>
          <div className="grid grid-cols-2 gap-1.5 mb-2">
            {([['aufgaben', 'Aufgaben'], ['lesetext', 'Lesetext'], ['lueckentext', 'Lückentext'], ['quiz', 'Quiz']] as const).map(([k, label]) => (
              <button key={k} onClick={() => setAiKind(k)}
                className={`px-2 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${aiKind === k ? 'bg-lilac-soft border-lilac text-lilac-deep' : 'border-rule text-ink-soft hover:bg-paper-deep'}`}>
                {label}
              </button>
            ))}
          </div>
          <textarea value={aiTopic} onChange={(e) => setAiTopic(e.target.value)}
            placeholder="Thema, z.B. der Igel" className="sl-input !h-16 !text-xs resize-none mb-2" />
          {aiError && <p className="text-coral text-[11px] mb-2">{aiError}</p>}
          <button onClick={handleGenerateAI} disabled={aiBusy || !aiTopic.trim()}
            className="sl-button sl-button-primary w-full justify-center gap-2 text-xs !h-8 disabled:opacity-50">
            {aiBusy ? <><Loader2 className="w-4 h-4 animate-spin" />Generiere…</> : <><Sparkles className="w-4 h-4" />Generieren</>}
          </button>
          <p className="text-[10px] text-ink-mute mt-2">Nutzt das lokale Ollama-Modell. Inhalt wird am Cursor eingefügt.</p>
        </div>

        {/* KI-Bild – noch kommend */}
        <div className="sl-card p-4 opacity-80">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <ImageIcon className="w-3.5 h-3.5 text-lilac" />
              <span className="text-xs font-bold text-ink-soft uppercase tracking-wide">KI-Bild</span>
            </div>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-sun-soft text-sun-deep">Bald</span>
          </div>
          <p className="text-[11px] text-ink-mute">Passende Illustrationen & Cliparts per KI erzeugen — kommt in Kürze.</p>
        </div>

        {/* Mathe-Generator */}
        <div className="sl-card p-4">
          <div className="flex items-center gap-2 mb-3">
            <Calculator className="w-3.5 h-3.5 text-sage" />
            <span className="text-xs font-bold text-ink-soft uppercase tracking-wide">Rechen-Generator</span>
          </div>
          <div className="grid grid-cols-4 gap-1.5 mb-2">
            {([['add', '+'], ['sub', '−'], ['mul', '·'], ['div', ':']] as const).map(([op, sym]) => (
              <button key={op} onClick={() => setMathOp(op)}
                className={`py-1.5 rounded-lg text-sm font-bold border transition-colors ${mathOp === op ? 'bg-sage-soft border-sage text-sage-deep' : 'border-rule text-ink-soft hover:bg-paper-deep'}`}>
                {sym}
              </button>
            ))}
          </div>
          <div className="flex gap-2 mb-2">
            <label className="flex-1 text-[11px] text-ink-mute">Zahlenraum
              <select value={mathMax} onChange={(e) => setMathMax(Number(e.target.value))} className="sl-input !h-8 !text-xs mt-0.5">
                {[10, 20, 100, 1000].map((n) => <option key={n} value={n}>bis {n}</option>)}
              </select>
            </label>
            <label className="flex-1 text-[11px] text-ink-mute">Anzahl
              <select value={mathCount} onChange={(e) => setMathCount(Number(e.target.value))} className="sl-input !h-8 !text-xs mt-0.5">
                {[4, 6, 8, 10, 12, 16, 20].map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </label>
          </div>
          <button onClick={insertMath} className="sl-button sl-button-outline w-full justify-center gap-2 text-xs !h-8">
            <Plus className="w-4 h-4" />Aufgaben einfügen
          </button>
        </div>

        {/* Klassenstufe */}
        <div className="sl-card p-4">
          <div className="text-xs font-bold text-ink-soft uppercase tracking-wide mb-3">Klassenstufe</div>
          <div className="grid grid-cols-2 gap-2">
            {GRADE_PRESETS.map((p) => (
              <button key={p.id} onClick={() => changeGradeLevel(p.id)}
                className={`px-2 py-2 rounded-lg text-xs font-semibold border transition-colors ${gradeLevel === p.id ? 'bg-cobalt-soft border-cobalt text-cobalt-deep' : 'border-rule text-ink-soft hover:bg-paper-deep'}`}>
                {p.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-ink-mute mt-2">Bestimmt Schriftgröße & Lineatur.</p>
        </div>

        {/* Schriftart */}
        <div className="sl-card p-4">
          <div className="text-xs font-bold text-ink-soft uppercase tracking-wide mb-3">Schriftart</div>
          <select value={fontFamily} onChange={(e) => changeFont(e.target.value)} className="sl-input !h-9 !text-sm">
            {FONTS.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
          </select>
          <p className="text-[11px] text-ink-mute mt-2">Freie, fibel-/lesegerechte Schriften.</p>
        </div>

        {/* Rahmen */}
        <div className="sl-card p-4">
          <div className="text-xs font-bold text-ink-soft uppercase tracking-wide mb-3">Rahmen</div>
          <div className="grid grid-cols-2 gap-2">
            {FRAMES.map((f) => (
              <button key={f.id} onClick={() => changeFrame(f.id)}
                className={`px-2 py-2 rounded-lg text-xs font-semibold border transition-colors ${frame === f.id ? 'bg-cobalt-soft border-cobalt text-cobalt-deep' : 'border-rule text-ink-soft hover:bg-paper-deep'}`}>
                {f.label}
              </button>
            ))}
          </div>
          <p className="text-[11px] text-ink-mute mt-2">Schmuckrahmen ums ganze Blatt.</p>
        </div>

        {/* Gliederung */}
        <div className="sl-card p-4">
          <div className="flex items-center gap-2 mb-3"><List className="w-3.5 h-3.5 text-cobalt" /><span className="text-xs font-bold text-ink-soft uppercase tracking-wide">Gliederung</span></div>
          {outline.length > 0 ? (
            <div className="space-y-1">
              {outline.map((o, i) => (
                <div key={i} className="text-xs text-ink-soft py-1 px-2 rounded-md hover:bg-paper-deep" style={{ paddingLeft: `${(o.level - 1) * 12 + 8}px` }}>{o.text || '—'}</div>
              ))}
            </div>
          ) : <div className="text-xs text-ink-mute py-2">Noch keine Überschriften.</div>}
        </div>

        {/* Details */}
        <div className="sl-card p-4">
          <div className="flex items-center gap-2 mb-3"><Info className="w-3.5 h-3.5 text-cobalt" /><span className="text-xs font-bold text-ink-soft uppercase tracking-wide">Details</span></div>
          <div className="space-y-1.5 text-xs">
            {[{ k: 'Titel', v: title || '—' }, { k: 'Fach', v: subject || '—' }, { k: 'Klasse', v: grade || '—' }, { k: 'Seiten', v: String(pageCount) }, { k: 'Status', v: status === 'saving' ? 'Speichert…' : status === 'unsaved' ? 'Ungespeichert' : status === 'error' ? 'Fehler' : docId ? 'Gespeichert' : 'Neu' }].map((r) => (
              <div key={r.k} className="flex justify-between gap-3"><span className="text-ink-mute">{r.k}</span><span className="font-semibold text-right">{r.v}</span></div>
            ))}
          </div>
        </div>
      </aside>
    </div>
  );
}
